import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchWorker, api } from "@/lib/api";
import CredentialCard from "@/components/CredentialCard";
import StatusBadge from "@/components/StatusBadge";
import QRCode from "@/components/QRCode";
import AddCredentialModal from "@/components/AddCredentialModal";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import IdentityPanel from "@/components/IdentityPanel";
import { Plus, Share2, ShieldCheck, MapPin, Briefcase, X } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_WORKER = "marcus-reed";

export default function WorkerWallet() {
  const { workerId } = useParams();
  const id = workerId || DEFAULT_WORKER;
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);   // credential object
  const [deleting, setDeleting] = useState(null); // credential object
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [identityVerified, setIdentityVerified] = useState(false);

  const handleIdentityStatus = useCallback((s) => {
    setIdentityVerified(s === "ProCred Identity Verified");
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    fetchWorker(id)
      .then(setWorker)
      .catch(() => toast.error("Failed to load wallet"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/credentials/${deleting.id}`);
      toast.success("Credential deleted");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete credential");
    } finally {
      setDeleteLoading(false);
    }
  };

  const verifyUrl =
    typeof window !== "undefined" ? `${window.location.origin}/verify/${id}` : "";

  if (loading || !worker) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-center text-slate-500" data-testid="wallet-loading">
        Loading wallet…
      </div>
    );
  }

  return (
    <div data-testid="worker-wallet-page" className="bg-[#F8FAFC] min-h-[calc(100vh-65px)]">
      <div className="mx-auto max-w-3xl px-6 py-10 md:py-14">
        {/* Page eyebrow */}
        <div className="mb-6 flex items-center justify-between">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
            01 / Worker · Digital Wallet
          </div>
          <Link
            to="/verify/marcus-reed"
            data-testid="wallet-preview-scan"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#FF5A1F] hover:underline"
          >
            Preview foreman scan →
          </Link>
        </div>

        {/* Profile card */}
        <div
          data-testid="worker-profile-card"
          className="relative overflow-hidden border border-slate-200 bg-white"
        >
          <div className="absolute inset-x-0 top-0 h-1.5 bg-[#0B1221]" />
          <div className="absolute right-0 top-0 h-1.5 w-1/3 bg-[#FF5A1F]" />

          <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-start sm:gap-6">
            <div className="flex flex-col items-center sm:items-start">
              <div
                data-testid="worker-identity-photo"
                className="relative h-24 w-24 shrink-0 overflow-hidden border border-slate-200 bg-slate-100"
              >
                {worker.photo_url ? (
                  <img
                    src={worker.photo_url}
                    alt={`${worker.name} — verified identity photo`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center font-display text-2xl font-black text-slate-400">
                    {worker.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                )}
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Identity Photo
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1
                  data-testid="worker-name"
                  className="font-display text-2xl font-bold tracking-tight text-[#0B1221] sm:text-3xl"
                >
                  {worker.name}
                </h1>
                {worker.verified && (
                  <span
                    data-testid="worker-verified-badge"
                    className="inline-flex items-center gap-1 border border-[#0B1221] bg-[#0B1221] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
                  >
                    <ShieldCheck className="h-3 w-3 text-[#FF5A1F]" strokeWidth={2.5} />
                    ProCred Verified
                  </span>
                )}
                <span
                  data-testid="worker-identity-badge"
                  className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                    identityVerified
                      ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-[#FF5A1F]"
                      : "border-slate-300 bg-slate-100 text-slate-500"
                  }`}
                >
                  <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
                  {identityVerified ? "Identity Verified" : "Identity Pending"}
                </span>
              </div>
              <div className="mt-1 text-sm font-medium text-slate-700">{worker.trade}</div>
              <div className="mt-2 grid grid-cols-1 gap-1.5 text-xs text-slate-500 sm:grid-cols-2">
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
                  {worker.location}
                </div>
                <div className="flex items-center gap-1.5">
                  <Briefcase className="h-3.5 w-3.5" strokeWidth={2} />
                  {worker.employer}
                </div>
              </div>
              <p
                data-testid="identity-photo-helper"
                className="mt-3 border-t border-slate-100 pt-3 text-xs leading-snug text-slate-500"
              >
                This verified identity photo is used by employers and foremen to confirm the worker presenting credentials is the verified credential holder.
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 border-t border-slate-200">
            <button
              data-testid="wallet-add-credential"
              onClick={() => setShowAdd(true)}
              className="flex items-center justify-center gap-2 border-r border-slate-200 bg-white px-4 py-3.5 text-sm font-semibold uppercase tracking-wider text-[#0B1221] transition-colors hover:bg-slate-50"
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              Add Credential
            </button>
            <button
              data-testid="wallet-share-qr"
              onClick={() => setShowQR(true)}
              className="flex items-center justify-center gap-2 bg-[#FF5A1F] px-4 py-3.5 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#e94d14]"
            >
              <Share2 className="h-4 w-4" strokeWidth={2.5} />
              Share QR
            </button>
          </div>
        </div>

        {/* Credentials section */}
        <div className="mt-10">
          <div className="mb-4">
            <IdentityPanel workerId={id} onStatusChange={handleIdentityStatus} />
          </div>
          <div className="mb-4 flex items-end justify-between border-b border-slate-200 pb-3">
            <h2 className="font-display text-xl font-bold tracking-tight text-[#0B1221]">
              Credentials
            </h2>
            <div data-testid="credential-count" className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              {worker.credentials.length} on file
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {worker.credentials.map((c, idx) => (
              <CredentialCard
                key={c.id}
                credential={c}
                testid={`credential-card-${idx}`}
                onEdit={() => setEditing(c)}
                onDelete={() => {
                  if (c.verification === "ProCred Verified") {
                    toast.error(
                      "Verified credentials cannot be modified by the worker. Contact the issuing organization or submit an updated credential for verification.",
                      { duration: 8000, id: `verified-lock-${c.id}` }
                    );
                    return;
                  }
                  setDeleting(c);
                }}
              />
            ))}
          </div>
        </div>

        {/* Clearance summary */}
        {worker.clearance && (
          <div
            data-testid="wallet-clearance-summary"
            className="mt-8 border border-slate-200 bg-white p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                  Current job-site clearance
                </div>
                <div className="font-display mt-1 text-lg font-bold text-[#0B1221]">
                  {worker.clearance.label}
                </div>
              </div>
              <StatusBadge status={worker.clearance.clearance} testid="wallet-clearance-badge" />
            </div>
          </div>
        )}
      </div>

      {/* QR Modal */}
      {showQR && (
        <div
          data-testid="qr-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1221]/70 p-6"
          onClick={() => setShowQR(false)}
        >
          <div
            className="relative w-full max-w-sm border border-slate-200 bg-white p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="qr-modal-close"
              onClick={() => setShowQR(false)}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center text-slate-400 hover:text-[#0B1221]"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              Share at the gate
            </div>
            <h3 className="font-display mt-1 text-2xl font-bold tracking-tight text-[#0B1221]">
              {worker.name}
            </h3>
            <p className="mt-1 text-sm text-slate-600">{worker.trade}</p>

            <div className="mt-5 flex items-center justify-center border border-slate-200 bg-white p-5">
              <QRCode value={verifyUrl} size={220} testid="wallet-qr-svg" />
            </div>

            <div className="mt-5 border-t border-slate-100 pt-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Verification link
              </div>
              <div className="mt-1 break-all font-mono text-xs text-slate-700">
                {verifyUrl}
              </div>
            </div>

            <Link
              to={`/verify/${worker.id}`}
              data-testid="qr-modal-open-verify"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-[#FF5A1F] px-4 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#e94d14]"
            >
              Open Foreman View
            </Link>
          </div>
        </div>
      )}

      {showAdd && (
        <AddCredentialModal
          mode="add"
          workerId={worker.id}
          onClose={() => setShowAdd(false)}
          onSaved={load}
        />
      )}

      {editing && (
        <AddCredentialModal
          mode="edit"
          credential={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}

      <ConfirmDeleteDialog
        open={!!deleting}
        title="Delete credential?"
        description={deleting ? `This permanently removes "${deleting.name}" from ${worker.name}'s wallet.` : ""}
        loading={deleteLoading}
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
